import { useEffect, useRef, useState } from "react";
import CONFIG from "../../config";

import "./LGuideMain.css"

export default function LGuideMain() {
    const MIN_DRAG_START = 15;
    const collapsedHeight = window.innerHeight - 340;
    const expandedHeight = window.innerHeight - 61;

    const [height, setHeight] = useState(collapsedHeight);
    const [transitioning, setTransitioning] = useState(false);
    const [pageIndex, setPageIndex] = useState(0);
    const [dragX, setDragX] = useState(0);
    const [dragY, setDragY] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const [weekPages, setWeekPages] = useState([]);

    const guideRef = useRef(null);
    const touchStartX = useRef(0);
    const touchStartY = useRef(0);
    const listRefs = useRef([]);
    const progress = (height - collapsedHeight) / (expandedHeight - collapsedHeight);
    const collapsedOpacity = 1 - progress * 2;
    const expandedOpacity = progress;

    useEffect(() => {
        const fetchWeekGuide = async () => {
            try {
                const res = await fetch(`${CONFIG.apiBase}/tasks/week-guide`);
                if (!res.ok) throw new Error("Failed to fetch week guide");

                const data = await res.json();
                setPages(data);
            } catch (err) {
                console.error("Error fetching week guide:", err);
            }
        };

        if (progress >= 0.1) {
            fetchWeekGuide();
        }
    }, [progress]);

    const setPages = (data) => {
        const today = new Date();
        const year = today.getUTCFullYear();
        const month = today.getUTCMonth();
        const date = today.getUTCDate();

        const timeFormattedTasks = data.map((d) => {
            const formattedTasks = d.tasks.map((t) => {
                const [hours, minutes] = t.time.split(":").map(Number);

                const utcDate = new Date(Date.UTC(year, month, date, hours, minutes));

                const localTime = utcDate.toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: false,
                });

                return {
                    id: t.id,
                    time: localTime,
                    name: t.name,
                    desc: t.desc,
                };
            });

            return { ...d, tasks: formattedTasks };
        });

        setWeekPages(timeFormattedTasks);
    };

    const animateHeight = (targetHeight) => {
        if (targetHeight >= collapsedHeight) {
            const el = guideRef.current;
            if (!el) return;

            el.style.height = `${height}px`;
            void el.offsetHeight;
            el.style.transition = "height 0.3s ease";

            requestAnimationFrame(() => {
                el.style.height = `${targetHeight}px`;
            });

            setTimeout(() => setHeight(targetHeight), 100);
        } else {
            setTransitioning(false);

            void guideRef.current?.offsetHeight;

            setTransitioning(true);

            requestAnimationFrame(() => {
                setHeight(targetHeight);
            });

            setTimeout(() => setTransitioning(false), 300);
        }
    };

    const onTabChevron = () => {
        setPageIndex(0)
        animateHeight(height === collapsedHeight ? expandedHeight : collapsedHeight);
    }

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                guideRef.current &&
                !guideRef.current.contains(event.target) &&
                height !== collapsedHeight
            ) {
                animateHeight(collapsedHeight);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [height]);

    const handleTouchStart = (e) => {
        touchStartX.current = e.touches[0].clientX;
        touchStartY.current = e.touches[0].clientY;
        setIsDragging(false);
    };

    const handleTouchMove = (e) => {
        const diffX = e.touches[0].clientX - touchStartX.current;
        const diffY = e.touches[0].clientY - touchStartY.current;

        setDragY(diffY);

        if (!isDragging && Math.abs(diffX) > MIN_DRAG_START) {
            setIsDragging(true);
        }

        if (!isDragging) return;

        if ((pageIndex === 0 && diffX > 0) || (pageIndex === weekPages.length - 1 && diffX < 0)) {
            setDragX(diffX * 0.3);
        } else {
            setDragX(diffX);
        }
    };

    const handleTouchEnd = () => {
        const containerHeigth = guideRef.current?.offsetHeight ?? window.innerHeight;
        const containerWidth = guideRef.current?.offsetWidth ?? window.innerWidth;
        const dragThresholdY = containerHeigth / 2;
        const dragThresholdX = containerWidth / 4;

        if (height === collapsedHeight && dragY < -dragThresholdY / 2) {
            setIsDragging(false);
            setPageIndex(0)
            animateHeight(expandedHeight);
            setDragX(0);
            setDragY(0);
            return;
        } else if (height === expandedHeight && dragY > dragThresholdY / 1.5) {
            const currentList = listRefs.current[pageIndex];
            const isAtTop = !currentList || currentList.scrollTop <= 0;

            if (isAtTop) {
                setIsDragging(false);
                setPageIndex(0);
                animateHeight(collapsedHeight);
            }

            setIsDragging(false);
            setDragX(0);
            setDragY(0);
            return;
        }

        setIsDragging(false);

        if (dragX < -dragThresholdX) {
            if (pageIndex === weekPages.length - 1) {
                setDragX(0);
            } else {
                setPageIndex(i => i + 1);
                setDragX(0);
            }
        } else if (dragX > dragThresholdX / 4 && pageIndex === 0) {
            setDragX(0);
        } else if (dragX > dragThresholdX) {
            if (pageIndex !== 0) {
                setPageIndex(i => i - 1);
                setDragX(0);
            }
        } else {
            setDragX(0);
        }
    };

    const getTaskStatus = (task, nextTask, taskDate) => {
        const now = new Date();
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const taskDay = new Date(taskDate);
        taskDay.setHours(0, 0, 0, 0);

        if (taskDay.getTime() !== today.getTime()) return "upcoming";

        const [hours, minutes] = task.time.split(":").map(Number);
        const taskTime = new Date();
        taskTime.setHours(hours, minutes, 0, 0);

        let nextTaskTime;
        if (nextTask) {
            const [nextHours, nextMinutes] = nextTask.time.split(":").map(Number);
            nextTaskTime = new Date();
            nextTaskTime.setHours(nextHours, nextMinutes, 0, 0);
        }

        const endOfDay = new Date();
        endOfDay.setHours(23, 59, 59, 999);

        if (nextTaskTime) {
            if (now >= taskTime && now < nextTaskTime) return "active";
            if (now >= nextTaskTime) return "inactive";
        } else {
            if (now >= taskTime && now <= endOfDay) return "active";
            if (now > endOfDay) return "inactive";
        }

        return "upcoming";
    };

    const colapsedGuide = () => {
        return (
            <div className="l-colapsed-guide" style={{
                opacity: collapsedOpacity,
                alignItems: progress < 0.05 ? "end" : "none",
                height: progress < 0.05 ? "100%" : "none",
                display: "flex",
            }}>
                <div className="l-guide-main-right"></div>
                <div className="l-chevron-left">
                    <button className="l-chevron-btn" onClick={onTabChevron}>
                        <i className="fa-solid fa-chevron-up"></i>
                    </button>
                </div>
                <div className="l-guide-main-left"></div>
            </div>
        );
    };

    const expandedGuide = () => {
        return (
            <div className="l-expanded-guide" style={{ opacity: expandedOpacity }}>
                {height < expandedHeight ? ""
                    : <>
                        <div className="l-chevron-right">
                            <button className="l-chevron-btn" onClick={onTabChevron}>
                                <i className="fa-solid fa-chevron-down"></i>
                            </button>
                        </div>
                        <div className="l-expanded-guide-content">
                            {expandedGuideHeader()}
                            {expandedGuideList()}
                        </div>
                    </>}
            </div>
        );
    };

    const expandedGuideHeader = () => {
        const colorLeftArrow = pageIndex === 0 ? "#666666ff" : "#F5F5F3";
        const colorRightArrow = pageIndex === weekPages.length - 1 ? "#666666ff" : "#F5F5F3";

        return (
            <div className="l-expanded-guide-title">
                <div className="l-text">UPCOMING NEXT WEEK</div>
                <div className="l-date-arrows">
                    <div className="l-left-arrow">
                        <button
                            style={{ color: colorLeftArrow }}
                            onClick={() => { setPageIndex((i) => Math.max(0, i - 1)) }}
                        >
                            <i className="fa-solid fa-chevron-left"></i>
                        </button>
                    </div>
                    <div className="l-day">{weekPages[pageIndex]?.label}</div>
                    <div className="l-right-arrow">
                        <button
                            style={{ color: colorRightArrow }}
                            onClick={() => {
                                if (pageIndex === weekPages.length - 1) {
                                    console.log("stretch effect here");
                                } else {
                                    setPageIndex((i) => Math.min(weekPages.length - 1, i + 1));
                                }
                            }}
                        >
                            <i className="fa-solid fa-chevron-right"></i>
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const expandedGuideList = () => {
        return (
            <div className="l-expanded-guide-list-main">
                <div
                    className="l-expanded-guide-list"
                    style={{
                        display: "flex",
                        width: `${weekPages.length * 100}%`,
                        transform: `translateX(calc(${-pageIndex * (100 / weekPages.length)}% + ${dragX}px))`,
                        transition: isDragging ? "none" : "transform 0.3s ease",
                    }}
                >
                    {weekPages.map((day, idx) => (
                        <div key={`${day.key}-${idx}`} className="l-page" style={{ flex: `0 0 calc(100% / ${weekPages.length})` }}>
                            <ul ref={(el) => (listRefs.current[idx] = el)}>
                                {day.tasks?.map((task, idx, arr) => {
                                    const nextTask = arr[idx + 1];
                                    const status = getTaskStatus(task, nextTask, weekPages[pageIndex]?.date);

                                    return (
                                        <li key={`${pageIndex}-${task.id}-${idx}`} className={`l-task-item ${status}`}>
                                            <div className="l-start-end-time">{task.time}</div>
                                            <div className="l-task-content">
                                                <div className="l-task-name">{task.name}</div>
                                                <div className="l-task-description">{task.desc}</div>
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    return (
        <div
            ref={guideRef}
            className="l-guide-main-container"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            style={{
                height: `${height}px`,
                transform: `translateY(0px)`,
                transition: transitioning ? "height 0.3s ease, transform 0.4s ease" : "none",
            }}>
            {progress > 0.95 ? ""
                : colapsedGuide()}
            {progress < 0.95 ? ""
                : expandedGuide()}
        </div>
    );
};